import type {
  WallboardAttendanceStatus,
  WallboardQuery,
} from '@/features/wallboard/types/wallboard.types'

export const WALLBOARD_ATTENDANCE_STATUS_VALUES = [
  'not_clocked_in',
  'clocked_in',
  'on_break',
  'clocked_out',
] as const satisfies readonly WallboardAttendanceStatus[]

function parseCsv(raw: string | null): string[] | undefined {
  if (!raw) return undefined
  const values = raw
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean)
  return values.length > 0 ? Array.from(new Set(values)).sort() : undefined
}

function filterAllowed<T extends string>(
  values: string[] | undefined,
  allowed: readonly T[],
): T[] | undefined {
  if (!values || values.length === 0) return undefined
  const allowedSet = new Set<string>(allowed)
  const filtered = values.filter((value): value is T => allowedSet.has(value))
  return filtered.length > 0 ? filtered : undefined
}

/** Canonicalize a query so equal filters produce equal cache keys / URLs. */
export function normalizeWallboardQuery(
  input: WallboardQuery | undefined,
): WallboardQuery {
  const companyIds =
    input?.companyIds && input.companyIds.length > 0
      ? Array.from(
          new Set(input.companyIds.map((value) => value.trim()).filter(Boolean)),
        ).sort()
      : undefined
  const search = input?.search?.trim() || undefined
  const attendanceStatus = filterAllowed(
    input?.attendanceStatus,
    WALLBOARD_ATTENDANCE_STATUS_VALUES,
  )
  return { companyIds, search, attendanceStatus }
}

export function parseWallboardSearchParams(
  searchParams: URLSearchParams,
): WallboardQuery {
  return normalizeWallboardQuery({
    companyIds: parseCsv(searchParams.get('companyIds')),
    search: searchParams.get('search') ?? undefined,
    attendanceStatus: filterAllowed(
      parseCsv(searchParams.get('attendanceStatus')),
      WALLBOARD_ATTENDANCE_STATUS_VALUES,
    ),
  })
}

export function buildWallboardSearchParams(
  input: WallboardQuery | undefined,
): URLSearchParams {
  const query = normalizeWallboardQuery(input)
  const params = new URLSearchParams()
  if (query.companyIds && query.companyIds.length > 0) {
    params.set('companyIds', query.companyIds.join(','))
  }
  if (query.search) params.set('search', query.search)
  if (query.attendanceStatus && query.attendanceStatus.length > 0) {
    params.set('attendanceStatus', query.attendanceStatus.join(','))
  }
  return params
}

/** Shape passed to axios `params` for the REST board request. */
export function wallboardQueryToParams(
  input: WallboardQuery | undefined,
): Record<string, unknown> {
  const query = normalizeWallboardQuery(input)
  const params: Record<string, unknown> = {}
  if (query.companyIds && query.companyIds.length > 0) {
    params.companyIds = query.companyIds.join(',')
  }
  if (query.search) params.search = query.search
  if (query.attendanceStatus && query.attendanceStatus.length > 0) {
    params.attendanceStatus = query.attendanceStatus.join(',')
  }
  return params
}
